/**
 * De eigen rekening van de verhuurder: het wachtwoord.
 *
 * Ontbrak, en het was het laatste gat van dit soort: er was een inlogpad, een sessie,
 * een wachtwoordhash en een `PasswordService` — alles behalve een scherm waarop een
 * verhuurder er iets mee kan. Anders dan een platformbeheerder heeft hij ook geen
 * opdrachtregel op de server als uitweg, dus wie zijn wachtwoord kwijt was of vermoedde
 * dat iemand het kende, kon niets (§17.45).
 *
 * Wat dit scherm expliciet zegt en wat geen sieraad is: het huidige wachtwoord is
 * nodig, en hoeveel andere sessies er zijn beëindigd. Het eerste omdat een sessie
 * overgenomen kan zijn; het tweede omdat wie wijzigt uit argwaan wil weten dát er
 * ergens anders iemand uit is gezet.
 */
import { useState } from 'react'
import type { FormEvent, ReactElement } from 'react'

import { MfaEnrollment } from '../components/mfa-enrollment'
import { authErrorMessageKey, changePassword } from '../lib/auth-client'
import { useTranslate } from '../lib/i18n-provider'
import { useSession } from '../lib/session-context'

/** Moet gelijk blijven aan `passwordField` in de API. */
const MIN_LENGTH = 12

export function AccountPage(): ReactElement {
  const t = useTranslate()
  const { user, impersonated, refresh } = useSession()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [repeat, setRepeat] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<number | null>(null)

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault()
    setError(null)
    setDone(null)

    // De herhaling wordt hier gecontroleerd en niet door de API: die kent er maar één,
    // en een typefout hoort niet als serverfout terug te komen.
    if (next !== repeat) {
      setError(t('dashboard.account.mismatch'))
      return
    }

    setBusy(true)
    const result = await changePassword(current, next)
    setBusy(false)

    if (!result.ok) {
      setError(t(authErrorMessageKey(result.error, 'password')))
      return
    }

    setCurrent('')
    setNext('')
    setRepeat('')
    setDone(result.data)
  }

  return (
    <section>
      <h1>{t('dashboard.account.title')}</h1>

      {user !== null ? (
        <p className="account__who">{t('dashboard.account.signedInAs', { email: user.email })}</p>
      ) : null}

      {/* Een beheerder die meekijkt mag dit niet, en dat hoort hij te zien vóór hij
          begint te typen — niet als foutmelding achteraf. De API weigert het ook;
          dit scherm herhaalt die grens alleen zichtbaar (§17.38). */}
      {impersonated ? (
        <p className="account__blocked" role="status">
          {t('dashboard.account.impersonated')}
        </p>
      ) : (
        <>
          {error !== null ? (
            <p className="account__error" role="alert">
              {error}
            </p>
          ) : null}
          {done !== null ? (
            <p className="account__done" role="status">
              {done === 0
                ? t('dashboard.account.changed')
                : t('dashboard.account.changedAndEnded', { count: done })}
            </p>
          ) : null}

          <form
            className="account__form"
            onSubmit={(event) => {
              void submit(event)
            }}
          >
            <label>
              <span>{t('dashboard.account.current')}</span>
              <input
                type="password"
                autoComplete="current-password"
                value={current}
                required
                onChange={(event) => {
                  setCurrent(event.target.value)
                }}
              />
            </label>
            <label>
              <span>{t('dashboard.account.new', { min: MIN_LENGTH })}</span>
              <input
                type="password"
                autoComplete="new-password"
                minLength={MIN_LENGTH}
                value={next}
                required
                onChange={(event) => {
                  setNext(event.target.value)
                }}
              />
            </label>
            <label>
              <span>{t('dashboard.account.repeat')}</span>
              <input
                type="password"
                autoComplete="new-password"
                minLength={MIN_LENGTH}
                value={repeat}
                required
                onChange={(event) => {
                  setRepeat(event.target.value)
                }}
              />
            </label>

            <p className="account__hint">{t('dashboard.account.hint')}</p>

            <button className="button button--primary" type="submit" disabled={busy}>
              {busy ? t('dashboard.account.saving') : t('dashboard.account.save')}
            </button>
          </form>

          {/* De tweede factor onder het wachtwoord: beide gaan over hoe goed dit
              account beveiligd is, en ze horen op één scherm. */}
          {user?.mfaEnabled === true ? (
            <div className="account__panel">
              <h2>{t('dashboard.account.mfa.title')}</h2>
              <p className="account__done">{t('dashboard.account.mfa.enabled')}</p>
            </div>
          ) : (
            <MfaEnrollment
              onConfirmed={() => {
                // De sessie opnieuw ophalen zodat `mfaEnabled` klopt en dit paneel
                // omslaat naar de bevestiging.
                void refresh()
              }}
            />
          )}
        </>
      )}
    </section>
  )
}
